/**
 * ROLLTISCH-Tab (Crime Ops)
 * Crafting-Interface für Tabak- und Cannabis-Verarbeitung.
 * Rezepte: Schneiden, Stopfen, Zigarre (legal); Joint, Blunt (illegal).
 */

import { api, ApiError } from '../../services/api.js';
import { getCleanItemName, renderItemBadges } from '../../utils/item-badges.js';
import { blaettchenPackCount, blaettchenBadgeCssVar } from '../../utils/blaettchen.js';
import { isPackItem, packCount, packBadgeCssVar } from '../../utils/pack-items.utils.js';
import { formatHideoutName } from '../../utils/hideout-format.js';
import { filterHideoutsByExpansion, renderHideoutSelectorTabs, renderHideoutSelectorEmpty } from '../../utils/hideout-selector.js';
import { DESKTOP_LAYOUT_QUERY } from '../../utils/mobile-layout.js';

// ─── Typen ────────────────────────────────────────────────────────────────────

interface BatchView {
  id:           string;
  recipeId:     string;
  recipeLabel:  string;
  primaryKey:   string;
  secondaryKey: string | null;
  quantity:     number;
  startedAt:    string;
  completesAt:  string;
  isDone:       boolean;
}

interface Equipment {
  hasTabakschneider:        boolean;
  hasStopfmaschine:         boolean;
  schneiderInInventory:     boolean;
  stopfmaschineInInventory: boolean;
}

interface InvEntry { key: string; quantity: number; }

interface RolltischStatus {
  batch:     BatchView | null;
  equipment: Equipment;
  inventory: {
    tobaccoDried:   InvEntry[];
    tobaccoCut:     InvEntry[];
    rollingSupply:  InvEntry[];
    rollingOutput:  InvEntry[];
    cannabisOutput: InvEntry[];
  };
  /**
   * Effektive Maximalmenge je Rezept-ID inkl. Talent-Bonus (Fließbandarbeit) — kommt
   * ausschließlich vom Backend. NIE selbst ableiten (keine Talent-ID, keine lokale 5/10 hier) —
   * das war exakt der Bug: dieses Frontend hat das Limit früher aus der statischen
   * /recipes-Antwort übernommen und kannte das Talent nicht (Muster wie `getDealerLimit()` in
   * crimeOps.ts, siehe NT-ROLLTISCH-BATCH-01).
   */
  maxQuantities: Record<string, number>;
}

interface RecipeDef {
  id:                   string;
  label:                string;
  description:          string;
  craftTimeMinutes:     number;
  maxQuantity:          number;
  permanentIngredients: string[];
  consumablesPerUnit:   Record<string, number>;
  outputMultiplier:     number;
  lore:                 string;
}

/** GET /rolltisch/recipes — Rezeptliste + globale Formel-Parameter für die Dauer-Vorschau. */
interface RecipesResponse {
  recipes:     RecipeDef[];
  setupShare:  number;
  perUnitShare: number;
}

interface Hideout {
  id:          string;
  subType:     string;
  customName:  string | null;
  nickname:    string | null;
  expansionType:  string | null;
  expansionLevel: number;
  inventory:   { itemType: string; itemKey: string; quantity: number; expiresAt: string | null }[];
}

// ─── State ────────────────────────────────────────────────────────────────────

let isMounted         = false;
let hideouts:           Hideout[] = [];
let selectedHideoutIdx  = 0;
let statusCache:        RolltischStatus | null = null;
let recipesCache:       RecipeDef[] | null = null;
/** Formel-Parameter der mengenabhängigen Dauer-Vorschau (kommt aus /rolltisch/recipes, NIE selbst geraten). */
let durationSetupShare:  number | null = null;
let durationPerUnitShare: number | null = null;
let selectedRecipeId    = '';
let selectedPrimaryKey  = '';
let selectedSecondaryKey = '';
let selectedQty         = 1;
let reloadPromise:      Promise<void> | null = null;
let batchTimer:         ReturnType<typeof setInterval> | null = null;
let clickHandler:       ((e: Event) => void) | null = null;
let changeHandler:      ((e: Event) => void) | null = null;

// ─── Label-Helfer ─────────────────────────────────────────────────────────────

/** Reiner Item-Anzeigename ohne Qualität/Variante (siehe item-badges.ts) — Badges werden separat gerendert. */
function itemLabel(key: string): string {
  return getCleanItemName(key);
}

/**
 * Farbcodiertes Badge für den Blättchen-Bestand.
 * Zeigt Packungsanzahl + Sheet-Gesamtzahl: z. B. "5x [210]".
 * Farbe basiert auf Gesamt-Sheets (Rot < 10, Gelb < 25, Grün ≥ 25).
 */
function blaettchenBadge(totalSheets: number): string {
  const cssVar  = blaettchenBadgeCssVar(totalSheets);
  const packs   = blaettchenPackCount(totalSheets);
  return `<span class="rt-q-badge" style="color:var(${cssVar});border-color:var(${cssVar});">${String(packs)}x [${String(totalSheets)}]</span>`;
}

/** NT-POLISH-01 (P7): generisches Pack-Badge (z. B. Zigarettenhülsen) — rot, wenn nicht mal 1 Einheit reicht. */
function packItemBadge(itemKey: string, totalUnits: number, neededPerUnit: number): string {
  const cssVar = totalUnits < neededPerUnit ? '--accent-red' : packBadgeCssVar(totalUnits);
  return `<span class="rt-q-badge" style="color:var(${cssVar});border-color:var(${cssVar});">${String(packCount(itemKey, totalUnits))}x [${String(totalUnits)}]</span>`;
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Live-Vorschau der mengenabhängigen Produktionsdauer — reproduziert exakt die Backend-Formel
 * (rolltisch.definitions.ts::calcRolltischDurationMinutes) mit den vom Server gelieferten
 * Parametern (durationSetupShare/durationPerUnitShare). Kein Zahlenwert wird hier erfunden,
 * nur die bereits vom Backend bestätigte Formel angewendet — analog zur Suchdauer-Vorschau im
 * Schwarzmarkt (estimateSearchMinutes). Vor dem Laden von /recipes: Fallback auf die flache
 * Basisdauer, damit die Anzeige nie leer/NaN ist.
 */
function previewDurationMinutes(recipe: RecipeDef, quantity: number): number {
  if (durationSetupShare === null || durationPerUnitShare === null) return recipe.craftTimeMinutes;
  return Math.ceil(recipe.craftTimeMinutes * (durationSetupShare + durationPerUnitShare * quantity));
}

function fmtCountdown(isoStr: string): string {
  const diff = Math.max(0, new Date(isoStr).getTime() - Date.now());
  const s = Math.floor(diff / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`;
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

function addRolltischStyles(): void {
  const id = 'rolltisch-tab-styles';
  let el = document.getElementById(id) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = `
    #rt-root { font-family: var(--font-mono); }
    .rt-section { margin-bottom: var(--space-4); }
    .rt-label {
      font-size: var(--font-size-xs); color: var(--text-muted);
      letter-spacing: 0.10em; text-transform: uppercase;
      margin: 0 0 var(--space-2) 0;
    }
    .rt-select {
      font-family: var(--font-mono); font-size: var(--font-size-xs);
      background: var(--bg-surface); color: var(--text-primary);
      border: 1px solid var(--border); border-radius: var(--radius);
      padding: 6px 8px; width: 100%; max-width: 360px;
    }
    .rt-equipment {
      display: flex; gap: var(--space-3); flex-wrap: wrap;
      margin-bottom: var(--space-3);
    }
    .rt-equip-badge {
      font-size: var(--font-size-xs); letter-spacing: 0.06em;
      padding: 3px 10px; border-radius: var(--radius);
      border: 1px solid var(--border); color: var(--text-muted);
    }
    .rt-equip-badge--ok { border-color: var(--accent-green); color: var(--accent-green); }
    .rt-equip-badge--missing { border-color: var(--accent-red); color: var(--accent-red); }
    .rt-batch-panel {
      background: var(--bg-elevated); border: 1px solid var(--border);
      border-radius: var(--radius); padding: var(--space-3);
      margin-bottom: var(--space-4);
    }
    .rt-batch-title { font-size: var(--font-size-sm); color: var(--text-primary); margin-bottom: var(--space-2); }
    .rt-progress-bar {
      height: 4px; background: var(--bg-surface); border-radius: 2px;
      overflow: hidden; margin-bottom: var(--space-2);
    }
    .rt-progress-fill { height: 100%; background: var(--accent-gold); transition: width 1s linear; }
    .rt-batch-meta { font-size: var(--font-size-xs); color: var(--text-muted); }
    .rt-recipe-grid {
      display: flex; flex-wrap: wrap; gap: var(--space-2); margin-bottom: var(--space-3);
    }
    .rt-recipe-btn {
      font-family: var(--font-mono); font-size: var(--font-size-xs);
      background: transparent; color: var(--text-muted);
      border: 1px solid var(--border); border-radius: var(--radius-btn);
      padding: 4px 12px; cursor: pointer; letter-spacing: 0.06em;
      transition: color 0.15s, border-color 0.15s;
    }
    .rt-recipe-btn:hover { color: var(--accent-gold); border-color: color-mix(in srgb, var(--accent-gold) 50%, transparent); }
    .rt-recipe-btn.is-active { color: var(--accent-gold); border-color: var(--accent-gold); }
    .rt-recipe-btn:disabled { opacity: 0.35; cursor: default; }
    .rt-form {
      background: var(--bg-elevated); border: 1px solid var(--border);
      border-radius: var(--radius); padding: var(--space-3);
      margin-bottom: var(--space-3);
    }
    .rt-form-row { display: flex; gap: var(--space-2); flex-wrap: wrap; align-items: flex-end; margin-bottom: var(--space-2); }
    .rt-form-group { display: flex; flex-direction: column; gap: 4px; }
    .rt-qty-select {
      font-family: var(--font-mono); font-size: var(--font-size-xs);
      background: var(--bg-surface); color: var(--text-primary);
      border: 1px solid var(--border); border-radius: var(--radius);
      padding: 6px 8px; width: 70px;
    }
    .rt-btn {
      font-family: var(--font-mono); font-size: var(--font-size-xs);
      background: transparent; border: 1px solid var(--border);
      border-radius: var(--radius-btn); padding: 6px 16px;
      cursor: pointer; letter-spacing: 0.06em; color: var(--text-muted);
    }
    .rt-btn--primary { border-color: var(--accent-gold); color: var(--accent-gold); }
    .rt-btn--primary:hover { background: color-mix(in srgb, var(--accent-gold) 10%, transparent); }
    .rt-btn--collect { border-color: var(--accent-green); color: var(--accent-green); }
    .rt-btn--collect:hover { background: color-mix(in srgb, var(--accent-green) 10%, transparent); }
    .rt-btn:disabled { opacity: 0.35; cursor: default; }
    .rt-recipe-lore { font-size: var(--font-size-xs); color: var(--text-italic); font-style: italic; margin-top: var(--space-1); }
    .rt-inv-table { width: 100%; border-collapse: collapse; font-size: var(--font-size-xs); }
    .rt-inv-table th { color: var(--text-muted); text-align: left; padding: 3px 6px; border-bottom: 1px solid var(--border); font-size: var(--font-size-xs); }
    .rt-inv-table td { padding: 3px 6px; color: var(--text-primary); border-bottom: 1px solid color-mix(in srgb, var(--border) 40%, transparent); }
    .rt-inv-type { color: var(--text-muted); font-size: var(--font-size-xs); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .rt-inv-unit { color: var(--text-muted); font-size: var(--font-size-xs); }
    .rt-q-badge {
      font-size: var(--font-size-xs); letter-spacing: 0.04em;
      padding: 1px 6px; border-radius: var(--radius-sm);
      border: 1px solid; display: inline-block;
    }
    .rt-empty { color: var(--text-muted); font-size: var(--font-size-xs); padding: var(--space-2) 0; }
    .rt-error-msg { color: var(--accent-red); font-size: var(--font-size-xs); margin-top: var(--space-2); }
    .rt-equipment-panel {
      background: var(--bg-elevated); border: 1px solid var(--border);
      border-radius: var(--radius); padding: var(--space-3);
      margin-bottom: var(--space-4); display: flex; flex-direction: column; gap: var(--space-2);
    }
    .rt-equip-row {
      display: flex; align-items: center; flex-wrap: wrap; gap: var(--space-1) var(--space-3);
    }
    .rt-equip-label {
      font-size: var(--font-size-sm); color: var(--text-primary);
      flex: 1 1 100%; min-width: 0; overflow-wrap: break-word;
    }
    .rt-equip-status {
      font-size: var(--font-size-xs); letter-spacing: 0.06em; min-width: 0;
    }
    .rt-equip-status--ok { color: var(--accent-green); }
    .rt-equip-hint { font-size: var(--font-size-xs); color: var(--text-muted); margin-left: auto; }
    .rt-btn--sm { padding: 3px 10px; font-size: var(--font-size-xs); margin-left: auto; flex-shrink: 0; }
    @media ${DESKTOP_LAYOUT_QUERY} {
      .rt-equip-label { flex: 0 1 auto; }
    }
    .rt-btn--install { border-color: var(--accent-green); color: var(--accent-green); }
    .rt-btn--install:hover { background: color-mix(in srgb, var(--accent-green) 10%, transparent); }
  `;
}

// ─── Render-Helfer ────────────────────────────────────────────────────────────

function renderEquipmentRow(
  installed: boolean,
  inInventory: boolean,
  label: string,
  itemKey: string,
  propertyId: string,
  hasBatch: boolean,
): string {
  let statusSpan: string;
  let actionBtn: string;

  if (installed) {
    statusSpan = `<span class="rt-equip-status rt-equip-status--ok">INSTALLIERT</span>`;
    actionBtn  = hasBatch
      ? `<button class="rt-btn rt-btn--sm" disabled title="Produktionslauf läuft">ABBAUEN</button>`
      : `<button class="rt-btn rt-btn--sm" data-rt-remove-equip="${esc(itemKey)}" data-rt-prop="${esc(propertyId)}">ABBAUEN</button>`;
  } else if (inInventory) {
    statusSpan = `<span class="rt-equip-status" style="color:var(--accent-orange);">IM LAGER</span>`;
    actionBtn  = `<button class="rt-btn rt-btn--sm rt-btn--install" data-rt-install-equip="${esc(itemKey)}" data-rt-prop="${esc(propertyId)}">INSTALLIEREN</button>`;
  } else {
    statusSpan = `<span class="rt-equip-status" style="color:var(--text-muted);">NICHT VORHANDEN</span>`;
    actionBtn  = `<span class="rt-equip-hint">Im Markt kaufen</span>`;
  }

  return `<div class="rt-equip-row">
    <span class="rt-equip-label">${esc(label)}</span>
    ${statusSpan}
    ${actionBtn}
  </div>`;
}

function renderEquipment(eq: Equipment, propertyId: string, hasBatch: boolean): string {
  return `<div class="rt-equipment-panel">
    ${renderEquipmentRow(eq.hasTabakschneider, eq.schneiderInInventory,     'Tabakschneider', 'tabakschneider', propertyId, hasBatch)}
    ${renderEquipmentRow(eq.hasStopfmaschine,  eq.stopfmaschineInInventory, 'Stopfmaschine',  'stopfmaschine',  propertyId, hasBatch)}
  </div>`;
}

function renderBatch(batch: BatchView | null): string {
  if (!batch) return '';
  const now = Date.now();
  const start = new Date(batch.startedAt).getTime();
  const end   = new Date(batch.completesAt).getTime();
  const pct   = Math.min(100, ((now - start) / (end - start)) * 100);

  if (batch.isDone) {
    return `<div class="rt-batch-panel">
      <p class="rt-batch-title">${esc(batch.recipeLabel)} — FERTIG (${batch.quantity}x)</p>
      <p class="rt-batch-meta">${esc(itemLabel(batch.primaryKey))}</p>
      <div style="margin-top:var(--space-2);">
        <button class="rt-btn rt-btn--collect" data-rt-collect="${esc(batch.id)}">ABHOLEN</button>
      </div>
      <p id="rt-collect-err" class="rt-error-msg" style="display:none;"></p>
    </div>`;
  }

  return `<div class="rt-batch-panel">
    <p class="rt-batch-title">${esc(batch.recipeLabel)} — LÄUFT (${batch.quantity}x)</p>
    <div class="rt-progress-bar"><div class="rt-progress-fill" style="width:${pct.toFixed(1)}%;"></div></div>
    <p class="rt-batch-meta" id="rt-countdown">Fertig in: ${fmtCountdown(batch.completesAt)}</p>
  </div>`;
}

function primaryInputsForRecipe(recipeId: string, status: RolltischStatus): InvEntry[] {
  if (recipeId === 'schneiden' || recipeId === 'zigarre') return status.inventory.tobaccoDried;
  if (recipeId === 'stopfen')                             return status.inventory.tobaccoCut;
  if (recipeId === 'joint_pur' || recipeId === 'joint_mix' || recipeId === 'blunt') return status.inventory.cannabisOutput;
  return [];
}

function secondaryInputsForRecipe(recipeId: string, status: RolltischStatus): InvEntry[] {
  if (recipeId === 'zigarre') {
    return status.inventory.tobaccoDried.filter((i) => /^deckblatt_getrocknet_q[1-5]$/.test(i.key));
  }
  if (recipeId === 'blunt') {
    return status.inventory.tobaccoDried.filter((i) => /^getrockneter_tabak_[a-z0-9_]+_q[1-5]$/.test(i.key));
  }
  if (recipeId === 'joint_mix') {
    return status.inventory.tobaccoCut;
  }
  return [];
}

function renderRecipeForm(status: RolltischStatus, recipes: RecipeDef[]): string {
  const recipe = recipes.find((r) => r.id === selectedRecipeId);

  const recipeButtons = recipes.map((r) => {
    const isActive = r.id === selectedRecipeId;
    const needsSchneider = r.permanentIngredients.includes('tabakschneider') && !status.equipment.hasTabakschneider;
    const needsStopf     = r.permanentIngredients.includes('stopfmaschine')  && !status.equipment.hasStopfmaschine;
    const disabled       = needsSchneider || needsStopf;
    const title = disabled
      ? (needsSchneider ? 'Tabakschneider fehlt' : 'Stopfmaschine fehlt')
      : r.description;
    return `<button
      class="rt-recipe-btn${isActive ? ' is-active' : ''}"
      data-rt-recipe="${esc(r.id)}"
      ${disabled ? 'disabled' : ''}
      title="${esc(title)}"
    >${esc(r.label.toUpperCase())}</button>`;
  }).join('');

  let formHtml = '';
  if (recipe) {
    const primaryItems  = primaryInputsForRecipe(recipe.id, status);
    const secondaryItems = secondaryInputsForRecipe(recipe.id, status);
    const hasSecondary  = secondaryItems.length > 0 || ['zigarre', 'blunt', 'joint_mix'].includes(recipe.id);
    const primaryOpts   = primaryItems.map((i) =>
      `<option value="${esc(i.key)}" ${i.key === selectedPrimaryKey ? 'selected' : ''}>${esc(itemLabel(i.key))} (${i.quantity}x)</option>`
    ).join('');
    const secondaryOpts = secondaryItems.map((i) =>
      `<option value="${esc(i.key)}" ${i.key === selectedSecondaryKey ? 'selected' : ''}>${esc(itemLabel(i.key))} (${i.quantity}x)</option>`
    ).join('');

    // Architekturregel (NT-ROLLTISCH-BATCH-01): kommt ausschließlich vom Server, nie aus
    // recipe.maxQuantity (das ist nur die talent-unabhängige Basismenge aus /recipes).
    // Fallback auf die Basismenge ist rein defensiv (sollte nie greifen — maxQuantities deckt
    // im Statuspayload jedes Rezept ab) und deckt keinen Talent-Bonus ab.
    const maxQty = status.maxQuantities[recipe.id] ?? recipe.maxQuantity;
    // Klemmen statt eine <option> ohne "selected" zu rendern (Browser würde sonst still auf die
    // erste Option zurückfallen, ohne dass selectedQty das nachvollzieht — z.B. wenn maxQty durch
    // frischen Talent-Erwerb während der Session sinkt/steigt).
    if (selectedQty > maxQty) selectedQty = maxQty;
    const qtyOpts = Array.from({ length: maxQty }, (_, i) => i + 1)
      .map((n) => `<option value="${n}" ${n === selectedQty ? 'selected' : ''}>${n}</option>`)
      .join('');
    const previewMinutes = previewDurationMinutes(recipe, selectedQty);

    // consumables info — Blättchen-Bestand als farbcodiertes Badge statt reinem Text
    const consumInfo = Object.entries(recipe.consumablesPerUnit).map(([k, n]) => {
      const have = status.inventory.rollingSupply.find((i) => i.key === k)?.quantity ?? 0;
      // NT-POLISH-01 (P7): Zigarettenhülsen wie Blättchen — "Pakete x [Einzelhülsen]".
      const haveDisplay = k === 'blaettchen'
        ? blaettchenBadge(have)
        : isPackItem(k) ? packItemBadge(k, have, n) : `${have}x vorhanden`;
      return `${esc(itemLabel(k))}: ${haveDisplay} (${n}/Stk. benötigt)`;
    }).join(' · ');

    formHtml = `<div class="rt-form">
      <p class="rt-label">${esc(recipe.label.toUpperCase())} — INPUTS</p>
      <div class="rt-form-row">
        <div class="rt-form-group">
          <label class="rt-label">HAUPTROHSTOFF</label>
          ${primaryItems.length === 0
            ? `<p class="rt-empty">Kein passender Rohstoff im Lager.</p>`
            : `<select class="rt-select" data-rt-primary style="max-width:280px;">${primaryOpts}</select>`
          }
        </div>
        ${hasSecondary ? `<div class="rt-form-group">
          <label class="rt-label">NEBENROHSTOFF</label>
          ${secondaryItems.length === 0
            ? `<p class="rt-empty">Kein passender Nebenrohstoff im Lager.</p>`
            : `<select class="rt-select" data-rt-secondary style="max-width:280px;">${secondaryOpts}</select>`
          }
        </div>` : ''}
        <div class="rt-form-group">
          <label class="rt-label">MENGE</label>
          <select class="rt-qty-select" data-rt-qty>${qtyOpts}</select>
        </div>
      </div>
      ${consumInfo ? `<p class="rt-batch-meta">${consumInfo}</p>` : ''}
      ${recipe.id === 'stopfen' ? `<p class="rt-batch-meta" style="color:var(--text-muted);">(8 Hülsen Schwund — 50 rein, 42 Zigaretten raus)</p>` : ''}
      ${recipe.outputMultiplier > 1 ? `<p class="rt-batch-meta" style="color:var(--text-muted);">(${recipe.outputMultiplier}x Output pro Einheit)</p>` : ''}
      <div style="margin-top:var(--space-2);display:flex;align-items:center;gap:var(--space-2);">
        <button class="rt-btn rt-btn--primary" data-rt-start
          ${primaryItems.length === 0 ? 'disabled' : ''}
        >STARTEN (${previewMinutes} Min)</button>
        <p id="rt-start-err" class="rt-error-msg" style="display:none;"></p>
      </div>
      <p class="rt-recipe-lore">${esc(recipe.lore)}</p>
    </div>`;
  }

  return `<div class="rt-section">
    <p class="rt-label">REZEPT WÄHLEN</p>
    <div class="rt-recipe-grid">${recipeButtons}</div>
    ${formHtml}
  </div>`;
}

function renderInventory(status: RolltischStatus): string {
  const allItems: { type: string; entry: InvEntry }[] = [
    ...status.inventory.tobaccoDried.map((e) => ({ type: 'Tabak (getrocknet)', entry: e })),
    ...status.inventory.tobaccoCut.map((e) => ({ type: 'Feinschnitt', entry: e })),
    ...status.inventory.rollingSupply.map((e) => ({ type: 'Verbrauchsmaterial', entry: e })),
    ...status.inventory.rollingOutput.map((e) => ({ type: 'Fertigprodukt', entry: e })),
    ...status.inventory.cannabisOutput.map((e) => ({ type: 'Cannabis-Ernte', entry: e })),
  ];
  if (allItems.length === 0) return `<p class="rt-empty">Kein relevanter Bestand im Versteck.</p>`;

  const rows = allItems.map(({ type, entry }) => {
    return `<tr>
      <td>${esc(itemLabel(entry.key))} ${renderItemBadges(entry.key, 'rt-q-badge')}</td>
      <td class="rt-inv-type">${esc(type)}</td>
      <td style="text-align:right;white-space:nowrap;">${String(entry.quantity)} <span class="rt-inv-unit">Stk.</span></td>
    </tr>`;
  }).join('');

  return `<div class="rt-section">
    <p class="rt-label">VERSTECK-INVENTAR (ROLLTISCH-RELEVANT)</p>
    <table class="rt-inv-table">
      <colgroup><col style="width:50%;"><col style="width:35%;"><col style="width:15%;"></colgroup>
      <thead><tr><th>ITEM</th><th>TYP</th><th style="text-align:right;">STÜCK</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
  </div>`;
}

// ─── Haupt-Render ─────────────────────────────────────────────────────────────

function renderContent(): string {
  if (hideouts.length === 0) {
    return renderHideoutSelectorEmpty('Kein Gewächshaus-Versteck vorhanden — im Tab IMMOBILIEN ausbauen.');
  }

  const hideoutSelector = renderHideoutSelectorTabs(
    hideouts.map((h, i) => ({ key: String(i), label: formatHideoutName(h).toUpperCase(), isSelected: i === selectedHideoutIdx })),
    'rt-htab',
  );

  if (!statusCache) {
    return hideoutSelector + '<p class="rt-empty">LADE...</p>';
  }

  const recipes = recipesCache ?? [];

  const currentHideout = hideouts[selectedHideoutIdx];
  const propertyId = currentHideout?.id ?? '';

  return hideoutSelector
    + `<div class="rt-section">${renderEquipment(statusCache.equipment, propertyId, statusCache.batch !== null)}</div>`
    + (statusCache.batch ? renderBatch(statusCache.batch) : '')
    + (statusCache.batch && !statusCache.batch.isDone ? '' : renderRecipeForm(statusCache, recipes))
    + renderInventory(statusCache);
}

export function renderRolltischTab(): string {
  addRolltischStyles();
  return `<div id="rt-root">${renderContent()}</div>`;
}

// ─── Daten laden ──────────────────────────────────────────────────────────────

async function loadStatus(): Promise<void> {
  const h = hideouts[selectedHideoutIdx];
  if (!h) return;
  statusCache = await api.get<RolltischStatus>(`/rolltisch/${encodeURIComponent(h.id)}/status`);
  // Defaults setzen wenn State leer
  if (!selectedPrimaryKey) {
    const primaries = primaryInputsForRecipe(selectedRecipeId, statusCache);
    selectedPrimaryKey = primaries[0]?.key ?? '';
  }
  if (!selectedSecondaryKey) {
    const secondaries = secondaryInputsForRecipe(selectedRecipeId, statusCache);
    selectedSecondaryKey = secondaries[0]?.key ?? '';
  }
}

async function reload(): Promise<void> {
  if (reloadPromise) return;
  reloadPromise = (async () => {
    try {
      if (hideouts.length === 0) {
        const res = await api.get<{ hideouts: Hideout[] }>('/hideouts');
        // Nur Verstecke mit aktivem Grow Room (Rolltisch verarbeitet Gras/Tabak aus dem Anbau)
        hideouts = filterHideoutsByExpansion(res.hideouts, 'GROW_ROOM');
        if (selectedHideoutIdx >= hideouts.length) selectedHideoutIdx = 0;
      }
      if (recipesCache === null) {
        const res = await api.get<RecipesResponse>('/rolltisch/recipes');
        recipesCache = res.recipes;
        durationSetupShare  = res.setupShare;
        durationPerUnitShare = res.perUnitShare;
        if (!selectedRecipeId && recipesCache.length > 0) {
          selectedRecipeId = recipesCache[0]?.id ?? '';
        }
      }
      await loadStatus();
    } finally {
      reloadPromise = null;
    }
    rerender();
  })();
  await reloadPromise;
}

function rerender(): void {
  const root = document.getElementById('rt-root');
  if (!root) return;
  addRolltischStyles();
  root.innerHTML = renderContent();
  startCountdownTimer();
}

// ─── Countdown-Timer ──────────────────────────────────────────────────────────

function startCountdownTimer(): void {
  if (batchTimer) { clearInterval(batchTimer); batchTimer = null; }
  if (!statusCache?.batch || statusCache.batch.isDone) return;
  const completesAt = statusCache.batch.completesAt;
  const timer = setInterval(() => {
    const el = document.getElementById('rt-countdown');
    if (!el) { clearInterval(timer); batchTimer = null; return; }
    const remaining = new Date(completesAt).getTime() - Date.now();
    if (remaining <= 0) {
      clearInterval(timer); batchTimer = null;
      // Server entscheidet ob isDone — clientseitiges Setzen würde bei Uhr-Abweichung flackern
      void reload();
      return;
    }
    el.textContent = `Fertig in: ${fmtCountdown(completesAt)}`;
  }, 1000);
  batchTimer = timer;
}

// ─── Event-Listener ───────────────────────────────────────────────────────────

function attachListeners(): void {
  const root = document.getElementById('rt-root');
  if (!root) return;

  clickHandler = (e: Event) => {
    const target = e.target as Element;

    // Equipment installieren
    const installBtn = target.closest<HTMLButtonElement>('[data-rt-install-equip]');
    if (installBtn && !installBtn.disabled) {
      const itemKey    = installBtn.dataset.rtInstallEquip ?? '';
      const propId = installBtn.dataset.rtProp ?? '';
      if (itemKey && propId) {
        installBtn.disabled = true;
        void handleAssignEquipment(propId, itemKey);
      }
      return;
    }

    // Equipment abbauen
    const removeBtn = target.closest<HTMLButtonElement>('[data-rt-remove-equip]');
    if (removeBtn && !removeBtn.disabled) {
      const itemKey    = removeBtn.dataset.rtRemoveEquip ?? '';
      const propId = removeBtn.dataset.rtProp ?? '';
      if (itemKey && propId) {
        removeBtn.disabled = true;
        void handleRemoveEquipment(propId, itemKey);
      }
      return;
    }

    // Versteck wechseln
    const htab = target.closest<HTMLButtonElement>('[data-rt-htab]');
    if (htab?.dataset.rtHtab !== undefined) {
      const val = parseInt(htab.dataset.rtHtab, 10);
      if (!isNaN(val) && val !== selectedHideoutIdx) {
        selectedHideoutIdx = val;
        statusCache = null;
        selectedPrimaryKey = '';
        selectedSecondaryKey = '';
        selectedQty = 1;
        rerender();
        void loadStatus().then(() => { rerender(); });
      }
      return;
    }

    // Rezept wählen
    const recipeBtn = target.closest<HTMLButtonElement>('[data-rt-recipe]');
    if (recipeBtn?.dataset.rtRecipe) {
      selectedRecipeId = recipeBtn.dataset.rtRecipe;
      selectedPrimaryKey = '';
      selectedSecondaryKey = '';
      selectedQty = 1;
      // Defaults aus Inventar setzen
      if (statusCache) {
        selectedPrimaryKey   = primaryInputsForRecipe(selectedRecipeId, statusCache)[0]?.key ?? '';
        selectedSecondaryKey = secondaryInputsForRecipe(selectedRecipeId, statusCache)[0]?.key ?? '';
      }
      rerender();
      return;
    }

    // Batch starten
    const startBtn = target.closest<HTMLButtonElement>('[data-rt-start]');
    if (startBtn) {
      void handleStart();
      return;
    }

    // Batch abholen
    const collectBtn = target.closest<HTMLButtonElement>('[data-rt-collect]');
    if (collectBtn?.dataset.rtCollect) {
      void handleCollect(collectBtn.dataset.rtCollect);
      return;
    }
  };

  changeHandler = (e: Event) => {
    const target = e.target as HTMLSelectElement;
    if (target.closest('[data-rt-primary]') || target.dataset.rtPrimary !== undefined) {
      selectedPrimaryKey = target.value;
      return;
    }
    if (target.closest('[data-rt-secondary]') || target.dataset.rtSecondary !== undefined) {
      selectedSecondaryKey = target.value;
      return;
    }
    if (target.closest('[data-rt-qty]') || target.dataset.rtQty !== undefined) {
      selectedQty = parseInt(target.value, 10) || 1;
      // Dauer ist jetzt mengenabhängig — Button-Text (Live-Vorschau) muss sich mitändern.
      rerender();
      return;
    }
  };

  root.addEventListener('click',  clickHandler);
  root.addEventListener('change', changeHandler);
}

function detachListeners(): void {
  const root = document.getElementById('rt-root');
  if (!root || !clickHandler || !changeHandler) return;
  root.removeEventListener('click',  clickHandler);
  root.removeEventListener('change', changeHandler);
  clickHandler  = null;
  changeHandler = null;
}

// ─── API-Aktionen ─────────────────────────────────────────────────────────────

async function handleStart(): Promise<void> {
  const errEl = document.getElementById('rt-start-err');
  if (errEl) { errEl.style.display = 'none'; }

  const h = hideouts[selectedHideoutIdx];
  if (!h || !selectedRecipeId || !selectedPrimaryKey) return;

  try {
    const body: Record<string, unknown> = {
      recipeId:   selectedRecipeId,
      primaryKey: selectedPrimaryKey,
      quantity:   selectedQty,
    };
    if (selectedSecondaryKey) body.secondaryKey = selectedSecondaryKey;

    await api.post(`/rolltisch/${encodeURIComponent(h.id)}/start`, body);
    selectedRecipeId    = '';
    selectedPrimaryKey  = '';
    selectedSecondaryKey = '';
    selectedQty = 1;
    await loadStatus();
    rerender();
  } catch (err) {
    const msg = err instanceof ApiError ? err.message : 'Fehler beim Starten.';
    if (errEl) { errEl.textContent = msg; errEl.style.display = 'block'; }
  }
}

async function handleAssignEquipment(propertyId: string, itemKey: string): Promise<void> {
  try {
    await api.post(`/rolltisch/${encodeURIComponent(propertyId)}/equipment`, { itemKey });
    await loadStatus();
    rerender();
  } catch (err) {
    const msg = err instanceof ApiError ? err.message : 'Installation fehlgeschlagen.';
    const errEl = document.getElementById('rt-start-err');
    if (errEl) { errEl.textContent = msg; errEl.style.display = 'block'; }
    rerender();
  }
}

async function handleRemoveEquipment(propertyId: string, itemKey: string): Promise<void> {
  try {
    await api.delete(`/rolltisch/${encodeURIComponent(propertyId)}/equipment/${encodeURIComponent(itemKey)}`);
    await loadStatus();
    rerender();
  } catch (err) {
    const msg = err instanceof ApiError ? err.message : 'Abbauen fehlgeschlagen.';
    const errEl = document.getElementById('rt-start-err');
    if (errEl) { errEl.textContent = msg; errEl.style.display = 'block'; }
    rerender();
  }
}

async function handleCollect(batchId: string): Promise<void> {
  const errEl = document.getElementById('rt-collect-err');
  if (errEl) { errEl.style.display = 'none'; }
  try {
    await api.post(`/rolltisch/batches/${encodeURIComponent(batchId)}/collect`, {});
    await loadStatus();
    rerender();
  } catch (err) {
    const msg = err instanceof ApiError ? err.message : 'Fehler beim Abholen.';
    if (errEl) { errEl.textContent = msg; errEl.style.display = 'block'; }
  }
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

export function mountRolltischTab(): void {
  if (isMounted) return;
  isMounted = true;
  attachListeners();
  void reload();
}

export async function refreshRolltischTab(): Promise<void> {
  if (!isMounted) return;
  hideouts = []; // Hideouts neu laden damit frische Daten kommen
  await reload();
}

export function cleanupRolltischTab(): void {
  if (batchTimer) { clearInterval(batchTimer); batchTimer = null; }
  detachListeners();
  hideouts          = [];
  statusCache       = null;
  recipesCache      = null;
  durationSetupShare   = null;
  durationPerUnitShare = null;
  selectedHideoutIdx  = 0;
  selectedRecipeId    = '';
  selectedPrimaryKey  = '';
  selectedSecondaryKey = '';
  selectedQty         = 1;
  reloadPromise       = null;
  isMounted           = false;
}
